<template>
  <div class="map-container">
    <!-- 左上角标题 -->
    <div class="header-title">
      <span class="icon-map">🗺️</span>
      全国无人车投放分布 (车辆动态轨迹)
    </div>

    <!-- ECharts 挂载点 -->
    <div ref="chartRef" class="echarts-box"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, shallowRef } from 'vue';
import * as echarts from 'echarts';
// 必须引入中国地图 GeoJSON 数据
import {provinceJson} from './province'

const chartRef = ref(null);
const chartInstance = shallowRef(null);

// // 模拟后端返回的有投放量的城市数据
// const mapData = [
//   { name: '浙江省', value: 88 },
//   { name: '广东省', value: 120 }, // 截图里的深绿色，数量最多
//   { name: '四川省', value: 45 },
//   { name: '江苏省', value: 30 },
//   { name: '北京', value: 18 } // 截图里 visualMap 最小值对应的颜色
// ];

const initChart = () => {
  chartInstance.value = echarts.init(chartRef.value);

  // 1. 注册地图数据 (给 ECharts 认识这个 JSON)
  // 注意：'china' 这个字符串是标识符，后面 series 里要对应
  echarts.registerMap('china', provinceJson);

};


const drawMap = (mapData) => {
  const option = {
    // 💥 需求 4：Tooltip 提示框 (只有有数据才展示)
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(13, 27, 49, 0.8)', // 还原截图暗色透明底
      borderColor: '#00d2ff',
      textStyle: { color: '#fff' },
      padding: [10, 15],
      // 使用 formatter 进行逻辑判断
      formatter: function (params) {
        // params.value 存在说明是有数据的城市；如果是 NaN/undefined 说明没点亮
        if (typeof params.value === 'number'  && !isNaN(params.value)) {
          // 返回自定义 HTML 结构匹配截图样式
          return `
            <div style="font-weight:bold; margin-bottom: 5px;">${params.name}</div>
            <div style="color: #ccc; font-size: 12px;">投放量: <span style="color:#fff; font-weight:bold;">${params.value}</span> 台</div>
          `;
        } else {
          // 如果没数据，返回空或不展示
          return '';
        }
      }
    },

    // 💥 需求 3 & 5：左下角的渐变色柱形图 (VisualMap)
    visualMap: {
      type: 'continuous',
      orient: 'vertical',
      left: '5%',
      bottom: '10%',
      min: 0,
      max: 150, // 根据你的实际数据最大值调整
      text: ['高', '低'], // 文本，默认为数值文本
      textStyle: { color: '#8a9ab9' },
      // 颜色过渡（对应截图：深蓝 -> 浅蓝 -> 绿）
      inRange: {
        color: ['#0047ff', '#00d2ff', '#00e573']
      },
      calculable: true, // 显示拖拽用的手柄
      hoverLink: true,  // 【需求 5 核心】打开后，鼠标在地图上悬浮，组件上对应的数值位置会自动出现一个小圆圈指示！
      itemWidth: 15,
      itemHeight: 120
    },

    // 💥 需求 1 & 2：地图主体配置
    series: [
      {
        type: 'map',
        map: 'china', // 对应上面 registerMap 注册的名字
        roam: false,  // 关闭鼠标缩放和平移（大屏通常固定死）
        zoom: 1.2,    // 初始缩放比例
        selectedMode: false,

        // 没数据的默认省份样式
        itemStyle: {
          areaColor: 'transparent', // 没点亮的省份透明
          // borderColor: 'rgba(43, 61, 91, 0.8)', // 暗色边界线
          // 1. 调亮边框颜色：改用带透明度的高亮青蓝色
          borderColor: 'rgba(0, 210, 255, 0.5)',
          borderWidth: 1.2,
          shadowColor: 'rgba(0, 210, 255, 0.2)',
          shadowBlur: 5,
          shadowOffsetX: 0,
          shadowOffsetY: 0
        },
        // 鼠标悬浮在高亮省份上的样式
        emphasis: {
          itemStyle: {
            areaColor: '#f1c40f', // 鼠标放上去变成亮黄色（可自定义）
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          },
          label: {
            show: false, // 悬浮时显示省份名字
            color: '#fff'
          }
        },
        // 默认省份文字标签（为了干净，默认不显示文字）
        label: {
          show: false
        },
        // 注入数据 (VisualMap 会自动根据这里的 value 匹配颜色渲染)
        data: mapData
      }
    ]
  };

  chartInstance.value.setOption(option);
}

defineExpose({
  initChart,drawMap
})
const handleResize = () => {
  if (chartInstance.value) chartInstance.value.resize();
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (chartInstance.value) chartInstance.value.dispose();
});
</script>

<style scoped>
/* 最外层容器样式，还原深色大屏背景 */
.map-container {
  width: 100%;
  height: 600px;
  background-color: #0b1426;
  border-radius: 8px;
  position: relative;
  font-family: 'Microsoft YaHei', sans-serif;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: inset 0 0 40px rgba(0, 210, 255, 0.05); /* 微弱的内部发光 */
}

/* 左上角标题 */
.header-title {
  position: absolute;
  top: 20px;
  left: 20px;
  color: #00d2ff;
  font-size: 16px;
  font-weight: bold;
  z-index: 10;
  display: flex;
  align-items: center;
}

.icon-map {
  margin-right: 8px;
  font-size: 18px;
}

/* 图表渲染区 */
.echarts-box {
  width: 100%;
  height: 100%;
}
</style>
